import {
  timeEntriesInfiniteQuery,
  timeEntryGroupPages,
} from "@common/api/time-entries"
import { ensureListData } from "@common/api/utils"
import { Button } from "@common/components/ui/button"
import { WO_PAGES } from "@common/flags"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router"
import { ActiveFilterBar } from "@/components/layout/active-filter-bar"
import { DisplayOptions } from "@/components/layout/display-options"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { requireFeatureFlag } from "@/lib/require-feature-flag"
import { requirePermission } from "@/lib/require-permission"
import { TimesheetTable } from "./-components/timesheet-table"
import {
  hasTimesheetFilters,
  NO_TIMESHEET_FILTERS,
  timesheetFilterConfigs,
} from "./-lib/filters"
import { GROUPING_LABELS, GROUPINGS } from "./-lib/grouping"
import {
  displayOf,
  groupingSearch,
  RANGES,
  rangeLabel,
  type TimesheetSearch,
  timesheetPagesParams,
  timesheetSearchSchema,
} from "./-lib/search"

export const Route = createFileRoute("/_layout/timesheet")({
  staticData: { breadcrumb: "Timesheet" },
  validateSearch: (search) => timesheetSearchSchema.parse(search),
  beforeLoad: async ({ context: { queryClient } }) => {
    await requirePermission(queryClient, "time_entry:record", "/")
    await requireFeatureFlag(queryClient, WO_PAGES)
  },
  loaderDeps: ({ search }) => ({
    pages: timesheetPagesParams(search),
    grouped: Boolean(displayOf(search).group),
  }),
  loader: ({ context: { queryClient }, deps }) =>
    ensureListData({
      grouped: deps.grouped,
      groups: () =>
        queryClient.ensureQueryData(timeEntryGroupPages.groups(deps.pages)),
      flat: () =>
        queryClient.ensureInfiniteQueryData(
          timeEntriesInfiniteQuery(deps.pages),
        ),
    }),
  component: TimesheetPage,
})

function useUpdateSearch(): (patch: Partial<TimesheetSearch>) => void {
  const navigate = useNavigate()
  return (patch) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}

function TimesheetPage(): React.ReactElement {
  const search = Route.useSearch()
  const update = useUpdateSearch()

  return (
    <ListPage toolbar={<Toolbar />} detail={null}>
      <ActiveFilterBar
        active={hasTimesheetFilters(search)}
        configs={timesheetFilterConfigs}
        values={search}
        onChange={update}
        onClear={() => update(NO_TIMESHEET_FILTERS)}
      />
      <TimesheetTable
        search={search}
        onClear={() => update(NO_TIMESHEET_FILTERS)}
      />
      <Outlet />
    </ListPage>
  )
}

function Toolbar(): React.ReactElement {
  const search = Route.useSearch()
  const update = useUpdateSearch()
  const display = displayOf(search)

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={RANGES}
          tab={search.range ?? "all"}
          label={rangeLabel}
          onSelect={(next) =>
            update({ range: next === "all" ? undefined : next })
          }
        />
      }
      actions={
        <>
          <FilterButton
            active={hasTimesheetFilters(search)}
            configs={timesheetFilterConfigs}
            onChange={update}
          />
          <DisplayOptions
            groupings={GROUPINGS}
            labels={GROUPING_LABELS}
            group={display.group}
            subgroup={display.subgroup}
            onChange={(next) => update(groupingSearch(next))}
          />
          <Button asChild size="sm">
            <Link to="/timesheet/log" search={true}>
              Log time
            </Link>
          </Button>
        </>
      }
    />
  )
}
